"use client";
import { Pagination } from "@/components/bnp-ui";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";

import { PropertyService, ExperimentalPropertyService } from "@/services";
import type { ExperimentalFeedSortKey } from "@/services";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import { useQuery } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { useEffect, useRef, useState } from "react";
import GoogleAd from "@/components/shared/GoogleAd";
import FooterAd from "@/components/shared/ads/FooterAd";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import ModernMinimalistFilters from "@/components/bnp-ui/property-filter/ModernPropertyFilters";

import parseHttpError from "@/utils/parseHttpError";
import { getAddress } from "@/utils/location";
import PropertyListingCard from "@/components/bnp-ui/listing-card/PropertyListingCard";
import PropertyListingSkeleton from "@/components/bnp-ui/listing-card/PropertyListingSkeleton";
import NoPropertyFound from "@/components/bnp-ui/helper_ui/NoPropertyFound";
import RequestErrorState from "@/components/bnp-ui/requestError";
import { Property } from "@/types/property";
import { track } from "@/utils/track";
import { ContentSidebar } from "@/components/layouts/Others/ContentSidebar";
import LocationRequest from "@/components/functional/LocationRequest";
import { buildPropertyDetailsUrl } from "@/utils/propertyReferrer";
import { useLocation } from "@/contexts/LocationContext";

const cleanQueryString = (
  val: string | string[] | undefined,
): string | undefined => {
  if (!val) return undefined;
  return Array.isArray(val) ? val[0] : val;
};

const cleanQueryBool = (val: any): boolean | undefined => {
  if (val === undefined || val === null || val === "") return undefined;
  return val === "true" || val === true;
};

const DEFAULT_REALTOR_ID = "97b86c8a-3598-4b3d-b13a-15042ca0fa94";

// Non-production builds exercise the experimental sort endpoints;
// production stays on the legacy feed endpoints.
const { isProduction: isProductionBuild } = getRuntimeEnvironment();

const Properties = () => {
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">(
    "default",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [screenWidth, setScreenWidth] = useState<number | null>(null);
  const { userLocation } = useLocation();
  const pageSize = 24;

  // NOTE: The backend only serves ads on the homepage for now. We fetch the
  // homepage ad set here so the placements render while testing; swap back to
  // AdPage.MANAGED_PROPERTIES once real page ads are available.
  const { inFeedAds, footerAds, headerAds, popupAds, sidebarAds } = usePageAds(
    AdPage.HOME,
  );

  const router = useRouter();

  const {
    location,
    state,
    city,
    bedroomCount,
    bathroomCount,
    listingType,
    propertyType,
    propertyCategory,
    realtorId,
    maxPrice,
    minPrice,
    furnished,
    serviced,
    shared,
    addedToSite,
    searchQuery,
    sort,
    page,
  } = router.query;

  const effectiveRealtorId = cleanQueryString(realtorId) || DEFAULT_REALTOR_ID;

  const trackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTrackRef = useRef<{ page: number; filterCount: number } | null>(
    null,
  );

  useEffect(() => {
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);

    trackTimerRef.current = setTimeout(() => {
      const filterCount = [
        location,
        state,
        city,
        bedroomCount,
        bathroomCount,
        listingType,
        propertyType,
        propertyCategory,
        realtorId,
        maxPrice,
        minPrice,
        furnished,
        serviced,
        shared,
        addedToSite,
        searchQuery,
      ].filter(Boolean).length;

      const snapshot = { page: currentPage, filterCount };
      const prev = prevTrackRef.current;

      // Skip if nothing meaningful changed (same page + same filter count).
      if (
        prev &&
        prev.page === snapshot.page &&
        prev.filterCount === snapshot.filterCount
      ) {
        return;
      }

      prevTrackRef.current = snapshot;

      track("user_explored_managed_props", {
        page: currentPage,
        ...(filterCount > 0 && { filter_count: filterCount }),
      });
    }, 300);

    return () => {
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    };
  }, [
    currentPage,
    location,
    state,
    city,
    bedroomCount,
    bathroomCount,
    listingType,
    propertyType,
    propertyCategory,
    realtorId,
    maxPrice,
    minPrice,
    furnished,
    serviced,
    shared,
    addedToSite,
    searchQuery,
  ]);

  const handleCardClick = (property: Property) => {
    router.push(
      buildPropertyDetailsUrl(property.id, { key: "managed-properties" }),
    );
  };

  const [isTopFilterExpanded, setIsTopFilterExpanded] = useState(false);

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
    } else {
      setCurrentPage(1);
    }
  }, [page]);

  useEffect(() => {
    const updateWidth = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const getPropertiesRequest = async () => {
    try {
      const queryParams = {
        ...(location && { location: cleanQueryString(location) }),
        ...(state && { state: cleanQueryString(state) }),
        ...(city && { city: cleanQueryString(city) }),
        ...(bedroomCount && { bedroomCount: cleanQueryString(bedroomCount) }),
        ...(bathroomCount && {
          bathroomCount: cleanQueryString(bathroomCount),
        }),
        ...(listingType && { listingType: cleanQueryString(listingType) }),
        ...(propertyType && { propertyType: cleanQueryString(propertyType) }),
        ...(propertyCategory && {
          propertyCategory: cleanQueryString(propertyCategory),
        }),
        ...(effectiveRealtorId && { realtorId: effectiveRealtorId }),
        ...(maxPrice && { maxPrice: cleanQueryString(maxPrice) }),
        ...(minPrice && { minPrice: cleanQueryString(minPrice) }),
        ...(furnished !== undefined && {
          furnished: cleanQueryBool(furnished),
        }),
        ...(serviced && { serviced: cleanQueryBool(serviced) }),
        ...(shared && { shared: cleanQueryBool(shared) }),
        ...(addedToSite && { addedToSite: cleanQueryBool(addedToSite) }),
        ...(searchQuery && { searchQuery: cleanQueryString(searchQuery) }),
        ...(sort && {
          sort: cleanQueryString(sort) as ExperimentalFeedSortKey,
        }),
        page: currentPage,
        pageSize,
      };

      let response;
      if (!queryParams.location && userLocation) {
        const address = await getAddress(userLocation.lat, userLocation.lng);
        const payloadWithLocation = {
          ...queryParams,
          userCurrentLocation: address,
          latitude: userLocation.lat.toString(),
          longitude: userLocation.lng.toString(),
        };
        response = isProductionBuild
          ? await PropertyService.GetManagedProperties(payloadWithLocation)
          : await ExperimentalPropertyService.GetManagedProperties(
              payloadWithLocation,
            );
      } else {
        response = isProductionBuild
          ? await PropertyService.GetManagedProperties(queryParams)
          : await ExperimentalPropertyService.GetManagedProperties(queryParams);
      }

      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        page: pageNumber.toString(),
      },
    });
  };

  const {
    data: propertiesData,
    isPending: propertiesIsPending,
    isError: propertiesIsError,
    refetch: propertiesRefetch,
  } = useQuery({
    queryKey: [
      "property",
      location,
      state,
      city,
      bedroomCount,
      bathroomCount,
      listingType,
      propertyType,
      propertyCategory,
      effectiveRealtorId,
      maxPrice,
      minPrice,
      furnished,
      serviced,
      shared,
      addedToSite,
      searchQuery,
      sort,
      currentPage,
      pageSize,
      userLocation?.lat,
      userLocation?.lng,
    ],
    queryFn: getPropertiesRequest,
    staleTime: 60 * 60 * 1000,
  });

  const sortProperties = (data: Property[]) => {
    if (sortOrder === "default") return data;

    return [...data].sort((a, b) => {
      const aValue = a.total_package || 0;
      const bValue = b.total_package || 0;
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });
  };

  // Derive the displayed list directly from the query data (instead of
  // syncing into local state via an effect) so the first settled render
  // never paints the empty state with a stale empty list.
  const properties = sortProperties(propertiesData?.data ?? []);

  return (
    <HomeWrapper
      footerSlot={footerAds[0] ? <FooterAd ad={footerAds[0]} /> : undefined}
    >
      {headerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
        </div>
      )}

      <>
        <div className="w-full container mx-auto gap-4 mb-5 sticky top-[64px] lg:top-[94px] z-20 bg-white">
          <ModernMinimalistFilters />
        </div>
      </>

      <ContentSidebar
        sidebarContent={
          sidebarAds?.[0] && (
            <div className="w-full mx-auto px-4 pt-4 pb-2">
              <AdRenderer ad={sidebarAds[0]} placement={AdPlacement.SIDEBAR} />
            </div>
          )
        }
        topOffset={200}
      >
        <div className={containerWidth}>
          <div className="flex flex-col items-center w-full  mb-8 lg:mb-16 overflow-x-clip">
            <LocationRequest />
            {propertiesIsPending ? (
              <PropertyListingSkeleton />
            ) : propertiesIsError ? (
              <RequestErrorState
                title="Failed to load properties"
                errorMessage="We couldn't retrieve properties at this time. Please try again."
                onRetry={async () => {
                  await propertiesRefetch();
                }}
              />
            ) : (
              <>
                {properties.length > 0 ? (
                  <>
                    <div className="flex flex-col lg:w-fit w-[100%]">
                      <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full">
                        {properties.slice(0, 6).map((property: Property) => (
                          <PropertyListingCard
                            key={property.id}
                            property={property}
                            onCardClick={handleCardClick}
                          />
                        ))}
                      </div>
                    </div>
                    {/* In-feed ad placed between the first two rows and the
                          second two rows (after the 6th property) */}
                    {inFeedAds[0] && properties.length > 6 && (
                      <div className="col-span-1 md:col-span-2 lg:col-span-3 w-full my-4">
                        <AdRenderer
                          ad={inFeedAds[0]}
                          placement={AdPlacement.IN_FEED}
                        />
                      </div>
                    )}

                    <div className="flex flex-col lg:w-fit w-[100%]">
                      {" "}
                      <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full">
                        {properties.slice(6).map((property: Property) => (
                          <PropertyListingCard
                            key={property.id}
                            property={property}
                            onCardClick={handleCardClick}
                          />
                        ))}
                      </div>
                    </div>
                    <Pagination
                      metadata={propertiesData?.metadata}
                      currentPage={currentPage}
                      onPageChange={handlePageChange}
                      className="w-full mt-8"
                      pageSize={pageSize}
                    />
                  </>
                ) : (
                  <NoPropertyFound />
                )}
              </>
            )}
          </div>
        </div>
      </ContentSidebar>

      <div className=" w-[100%] lg:w-[800px] flex justify-center my-8">
        <GoogleAd
          adSlot={process.env.NEXT_PUBLIC_AD_SLOT_BANNER || ""}
          adFormat="horizontal"
          fullWidthResponsive={true}
          className="w-full"
        />
      </div>
      {popupAds[0] && (
        <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
      )}

      {footerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
        </div>
      )}
    </HomeWrapper>
  );
};

export default Properties;

/*

              {false && properties.length > 0 && !propertiesIsLoading && (
                <div className="hidden lg:block lg:min-w-[300px]">
                  <div className="w-full mt-4 mb-4">
                    <select
                      value={sortOrder}
                      onChange={handleSortChange}
                      className="px-4 py-2 border rounded-md bg-white"
                    >
                      <option value="default">Default Order</option>
                      <option value="asc">Price: Low to High</option>
                      <option value="desc">Price: High to Low</option>
                    </select>
                  </div>

                  <PropertyFilters />
                  {sidebarAds[0] && (
                    <div className="mt-4">
                      <SidebarAd ad={sidebarAds[0]} sticky stickyOffset={80} />
                    </div>
                  )}

                  <div className="sticky top-20 z-10 mt-4">
                    <GoogleAd
                      adSlot={process.env.NEXT_PUBLIC_AD_SLOT_SIDEBAR || ""}
                      adFormat="rectangle"
                      className="w-full"
                    />
                  </div>
                </div>
              )}
*/
